import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import React, { ComponentPropsWithRef } from 'react'
import Grid from '@mui/material/Grid2'
import { useTranslation } from 'react-i18next'
import { CountryIso2, defaultCountries, FlagImage, parseCountry, usePhoneInput } from 'react-international-phone'
import CaleoInputLabel from 'Components/reusable/CaleoCustomComponents/CaleoInputLabel'

interface OwnProps {
  id?: string
  value: string
  error?: string | boolean
  helperText?: string | null
  label?: React.ReactNode
  required?: boolean
  insetLabel?: boolean
  onChange: (value: string) => void
}

const PhoneNumberInput: React.FC<OwnProps & Omit<ComponentPropsWithRef<typeof TextField>, keyof OwnProps>> = ({
  id,
  value,
  error,
  helperText,
  label,
  required,
  insetLabel,
  onChange,
  ...rest
}) => {
  const { t } = useTranslation()
  const generatedId = React.useId()
  const resolvedId = id ?? generatedId

  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } = usePhoneInput({
    defaultCountry: 'fi',
    value: value ?? '+358',
    countries: defaultCountries,
    onChange: ({ phone }) => {
      onChange(phone)
    },
  })

  return (
    <Grid sx={{ display: 'flex', alignItems: 'baseline', flexDirection: 'column' }}>
      {!insetLabel && <CaleoInputLabel htmlFor={resolvedId} required={required} label={label} />}
      <TextField
        id={resolvedId}
        data-testid={id ? `field-${id}` : 'field-phone-number'}
        margin="dense"
        error={!!error}
        label={
          insetLabel ? (
            <>
              {label}
              <span style={{ color: '#BE3C3C', fontSize: '0.85em', marginLeft: '5px' }}>
                {required && `${t('required')}`}
              </span>
            </>
          ) : undefined
        }
        variant="outlined"
        helperText={error ? error : helperText}
        required={required}
        type="tel"
        size="small"
        value={inputValue}
        onChange={handlePhoneValueChange}
        inputRef={inputRef}
        {...rest}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start" style={{ marginRight: '2px', marginLeft: '-8px' }}>
                <Select
                  data-testid={id ? `field-${id}-country-code` : 'field-phone-number-country-code'}
                  inputProps={{ 'aria-label': t('countryCode') }}
                  MenuProps={{
                    style: {
                      height: '300px',
                      width: '360px',
                      top: '10px',
                      left: '-34px',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
                  sx={{
                    width: 'max-content',
                    // Remove default outline (display only on focus)
                    fieldset: {
                      display: 'none',
                    },
                    '&.Mui-focused:has(div[aria-expanded="false"])': {
                      fieldset: {
                        display: 'block',
                        border: 'none',
                      },
                    },
                    // Update default spacing
                    '.MuiSelect-select': {
                      padding: '8px',
                      paddingRight: '24px !important',
                    },
                    svg: {
                      right: 0,
                    },
                  }}
                  value={country.iso2}
                  onChange={e => setCountry(e.target.value as CountryIso2)}
                  renderValue={value => (
                    <FlagImage iso2={value} style={{ display: 'flex', alignSelf: 'center', minWidth: '1.3em' }} />
                  )}
                >
                  {defaultCountries.map(c => {
                    const country = parseCountry(c)
                    return (
                      <MenuItem key={country.iso2} value={country.iso2}>
                        <FlagImage iso2={country.iso2} style={{ marginRight: '8px', maxHeight: '1.3em' }} />
                        <Typography marginRight="8px">{country.name}</Typography>
                        <Typography color="gray">+{country.dialCode}</Typography>
                      </MenuItem>
                    )
                  })}
                </Select>
              </InputAdornment>
            ),
          },

          inputLabel: insetLabel ? { shrink: true } : {},
          formHelperText: { style: { textAlign: 'right' } },
        }}
      />
    </Grid>
  )
}

export default PhoneNumberInput
